import isNil from 'lodash/isNil';

import { metricsAxios } from 'api/apiBase/axios';
import { mapError } from 'api/apiBase/mapError';
import { mapResponse } from 'api/apiBase/mapResponse';

import {
  GetCexActualBalanceRequest,
  GetCexActualBalanceResponse,
  GetCexAvgOrderSizeRequest,
  GetCexAvgOrderSizeResponse,
  GetCexBalancesDeltaRequest,
  GetCexBalancesDeltaResponse,
  GetCexOurTradesCountRequest,
  GetCexOurTradesCountResponse,
  GetCexTotalFeesRequest,
  GetCexTotalFeesResponse,
} from './api.types';

/**
 * Retrieves average order size for requested pairs and accounts.
 *
 * @param req - Pair ids with optional period, date range, and account filters.
 * @param signal - Optional abort controller signal.
 * @returns Promise with mapped average order size response or mapped error.
 * @see https://api-metrics-dev.111pg.info/swagger/index.html#/volume/get_api_v1_avg_size
 */
export async function getCexAvgOrderSize(
  { pairId, period, startDate, endDate, account_id }: GetCexAvgOrderSizeRequest,
  signal?: AbortSignal,
) {
  try {
    const urlParams = new URLSearchParams();

    for (const id of pairId) {
      urlParams.append('pair_id', id.toString());
    }

    if (period) {
      urlParams.append('period', period);
    }

    if (!isNil(startDate) && !isNil(endDate)) {
      urlParams.append('from', startDate.toString());
      urlParams.append('to', endDate.toString());
    }

    if (account_id) {
      for (const accountId of account_id) {
        urlParams.append('account_id', accountId.toString());
      }
    }

    const response = await metricsAxios.get<GetCexAvgOrderSizeResponse>(
      '/api/v1/avg/size?' + urlParams.toString(),
      { signal },
    );

    return mapResponse({
      data: response.data,
    });
  } catch (error) {
    return mapError<GetCexAvgOrderSizeResponse>({ error });
  }
}

/**
 * Retrieves count of our trades for specified pairs and accounts.
 *
 * @param req - Pair ids with optional period, date range, and account filters.
 * @param signal - Optional abort controller signal.
 * @returns Promise with mapped trades count response or mapped error.
 * @see https://api-metrics-dev.111pg.info/swagger/index.html#/volume/get_api_v1_count_our_trade
 */
export async function getCexOurTradesCount(
  { pairId, period, startDate, endDate, account_id }: GetCexOurTradesCountRequest,
  signal?: AbortSignal,
) {
  try {
    const urlParams = new URLSearchParams();

    for (const id of pairId) {
      urlParams.append('pair_id', id.toString());
    }

    if (period) {
      urlParams.append('period', period);
    }

    if (!isNil(startDate) && !isNil(endDate)) {
      urlParams.append('from', startDate.toString());
      urlParams.append('to', endDate.toString());
    }

    if (account_id) {
      for (const accountId of account_id) {
        urlParams.append('account_id', accountId.toString());
      }
    }

    const response = await metricsAxios.get<GetCexOurTradesCountResponse>(
      '/api/v1/count/our/trade?' + urlParams.toString(),
      { signal },
    );

    return mapResponse({
      data: response.data,
    });
  } catch (error) {
    return mapError<GetCexOurTradesCountResponse>({ error });
  }
}

/**
 * Retrieves balances delta for provided symbol across exchanges/accounts.
 *
 * @param req - Symbol, exchanges, optional accounts, period, and date range filters.
 * @param signal - Optional abort controller signal.
 * @returns Promise with mapped balances delta response or mapped error.
 * @see https://api-metrics-dev.111pg.info/swagger/index.html#/balance/get_api_v1_balance_delta
 */
export async function getCexBalancesDelta(
  { symbol, cex, account_id, period, startDate, endDate }: GetCexBalancesDeltaRequest,
  signal?: AbortSignal,
) {
  try {
    const urlParams = new URLSearchParams();

    urlParams.append('symbol', symbol);

    for (const cexName of cex) {
      urlParams.append('cex', cexName.toString());
    }

    if (account_id) {
      for (const accountId of account_id) {
        urlParams.append('account_id', accountId.toString());
      }
    }

    if (period) {
      urlParams.append('period', period);
    }

    if (!isNil(startDate) && !isNil(endDate)) {
      urlParams.append('from', startDate.toString());
      urlParams.append('to', endDate.toString());
    }

    const response = await metricsAxios.get<GetCexBalancesDeltaResponse>(
      '/api/v1/balance/delta?' + urlParams.toString(),
      { signal },
    );

    return mapResponse({
      data: response.data,
    });
  } catch (error) {
    return mapError<GetCexBalancesDeltaResponse>({ error });
  }
}

/**
 * Retrieves total collected fees for requested pairs and accounts.
 *
 * @param req - Pair ids with optional period, date range, and account filters.
 * @param signal - Optional abort controller signal.
 * @returns Promise with mapped total fees response or mapped error.
 * @see https://api-metrics-dev.111pg.info/swagger/index.html#/volume/get_api_v1_total_our_fee
 */
export async function getCexTotalFees(
  { pairId, period, startDate, endDate, account_id }: GetCexTotalFeesRequest,
  signal?: AbortSignal,
) {
  try {
    const urlParams = new URLSearchParams();

    for (const id of pairId) {
      urlParams.append('pair_id', id.toString());
    }

    if (period) {
      urlParams.append('period', period);
    }

    if (!isNil(startDate) && !isNil(endDate)) {
      urlParams.append('from', startDate.toString());
      urlParams.append('to', endDate.toString());
    }

    if (account_id) {
      for (const accountId of account_id) {
        urlParams.append('account_id', accountId.toString());
      }
    }

    const response = await metricsAxios.get<GetCexTotalFeesResponse>(
      '/api/v1/total/our/fee?' + urlParams.toString(),
      { signal },
    );

    return mapResponse({
      data: response.data,
    });
  } catch (error) {
    return mapError<GetCexTotalFeesResponse>({ error });
  }
}

/**
 * Retrieves actual balance for a symbol on a specific exchange and account(s).
 *
 * @param req - Symbol, exchange, and optional account filters.
 * @param signal - Optional abort controller signal.
 * @returns Promise with mapped actual balance response or mapped error.
 * @see https://api-metrics-dev.111pg.info/swagger/index.html#/balance/get_api_v1_balance_actual
 */
export async function getCexActualBalance(
  { symbol, cex, account_id }: GetCexActualBalanceRequest,
  signal?: AbortSignal,
) {
  try {
    const urlParams = new URLSearchParams();

    urlParams.append('symbol', symbol);
    urlParams.append('cex', cex);

    if (account_id) {
      for (const accountId of account_id) {
        urlParams.append('account_id', accountId.toString());
      }
    }

    const response = await metricsAxios.get<GetCexActualBalanceResponse>(
      '/api/v1/balance/actual?' + urlParams.toString(),
      { signal },
    );

    return mapResponse({
      data: response.data,
    });
  } catch (error) {
    return mapError<GetCexActualBalanceResponse>({ error });
  }
}
